import React, { memo, useCallback, useContext, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { MdClose, MdContentCopy } from "react-icons/md";
import { callAPI } from "../helper/apiUtils";
import { $crud } from "../helper/CrudFactory";
import { $user } from "../helper/UserFactory";
import LanguageContext from "../helper/LanguageContext";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import remarkBreaks from "remark-breaks";
import { marked } from "marked";
import moment from "moment";
import "../styles/kjuupSupport.css";
import "./KjuupSupportModal.css";
import featureFlags from "../featureFlags";

const INITIAL_SUGGESTIONS_EN = [
  { label: "How to create a project?" },
  { label: "How to send a message?" },
  { label: "What is Kjuup Pilot?" },
  { label: "How to manage my profile?" },
];

const INITIAL_SUGGESTIONS_DE = [
  { label: "Wie erstelle ich ein Projekt?" },
  { label: "Wie sende ich eine Nachricht?" },
  { label: "Was ist Kjuup Pilot?" },
  { label: "Wie verwalte ich mein Profil?" },
];

function parseSuggestions(content) {
  if (!content) return { cleanContent: content, suggestions: [] };

  const separatorIdx = content.lastIndexOf("---");
  if (separatorIdx === -1) return { cleanContent: content, suggestions: [] };

  const afterSeparator = content.substring(separatorIdx + 3).trim();
  const beforeSeparator = content.substring(0, separatorIdx).trim();

  try {
    const jsonMatch = afterSeparator.match(/\[[\s\S]*\]/);
    if (jsonMatch) {
      const parsed = JSON.parse(jsonMatch[0]);
      if (Array.isArray(parsed) && parsed.length > 0 && parsed[0].label) {
        return { cleanContent: beforeSeparator, suggestions: parsed };
      }
    }
  } catch (e) {
    // Not valid JSON, fall through
  }

  const lines = afterSeparator.split("\n").filter((l) => l.trim());
  if (lines.length > 0) {
    const suggestions = lines
      .map((l) => l.replace(/^[▸•\-*]\s*/, "").trim())
      .filter(Boolean)
      .map((label) => ({ label }));
    if (suggestions.length > 0) {
      return { cleanContent: beforeSeparator, suggestions };
    }
  }

  return { cleanContent: content, suggestions: [] };
}

function getAskedTopics(chatHistory) {
  return chatHistory
    .filter((m) => m.role === "user")
    .map((m) => m.content.toLowerCase().trim());
}

function filterDuplicateSuggestions(suggestions, chatHistory) {
  const asked = getAskedTopics(chatHistory);
  return suggestions.filter((s) => {
    const label = s.label.toLowerCase().trim();
    return !asked.some(
      (q) => q === label || q.includes(label) || label.includes(q)
    );
  });
}

function KjuupSupportModal() {
  const user = $user.get();
  const { isGerm, strings } = useContext(LanguageContext);

  const [isOpen, setIsOpen] = useState(false);
  const [isMessageSending, setIsMessageSending] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [message, setMessage] = useState("");
  const [chatHistory, setChatHistory] = useState([]);
  const [hoveredChatId, setHoveredChatId] = useState(null);
  const [expandedMessages, setExpandedMessages] = useState({});
  const [currentSuggestions, setCurrentSuggestions] = useState([]);
  const [isClosing, setIsClosing] = useState(false);
  const [hasLoadedHistory, setHasLoadedHistory] = useState(false);

  const textRef = useRef(null);
  const messagesEndRef = useRef(null);
  const modalRef = useRef(null);

  const loadHistory = useCallback(async () => {
    if (!user?.id) return;
    setIsLoadingHistory(true);
    try {
      const { data } = await callAPI("/chat/kjuup-chat/history", {}, "GET");
      if (data?.history && Array.isArray(data.history) && data.history.length > 0) {
        const parsed = data.history.map((m) => {
          const { cleanContent, suggestions } = parseSuggestions(m.content);
          return { ...m, content: cleanContent, suggestions: suggestions || [] };
        });
        setChatHistory(parsed);
        const lastAssistant = [...parsed].reverse().find((m) => m.role === "assistant" && m.suggestions?.length > 0);
        if (lastAssistant) {
          setCurrentSuggestions(filterDuplicateSuggestions(lastAssistant.suggestions, parsed));
        } else {
          setCurrentSuggestions([]);
        }
      } else {
        setChatHistory([]);
        setCurrentSuggestions(isGerm ? INITIAL_SUGGESTIONS_DE : INITIAL_SUGGESTIONS_EN);
      }
    } catch (e) {
      console.error("[KjuupSupport] Failed to load history:", e);
    } finally {
      setIsLoadingHistory(false);
      setHasLoadedHistory(true);
    }
  }, [user?.id, isGerm]);

  useEffect(() => {
    if (!hasLoadedHistory && user?.id) {
      loadHistory();
    }
  }, [user?.id, hasLoadedHistory, loadHistory]);

  useEffect(() => {
    if (isOpen && chatHistory.length === 0 && hasLoadedHistory) {
      setCurrentSuggestions(isGerm ? INITIAL_SUGGESTIONS_DE : INITIAL_SUGGESTIONS_EN);
    }
  }, [isOpen, isGerm, chatHistory.length, hasLoadedHistory]);

  useEffect(() => {
    if (isOpen && textRef.current) {
      setTimeout(() => textRef.current?.focus(), 300);
    }
    if (isOpen && chatHistory.length > 0) {
      setTimeout(() => scrollToBottom(), 200);
    }
  }, [isOpen]);

  const scrollToBottom = useCallback(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, []);

  const handleOpen = () => {
    setIsClosing(false);
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsOpen(false);
      setIsClosing(false);
    }, 250);
  };

  const toggleExpand = (id) => {
    setExpandedMessages((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleCopy = (chat) => {
    const copyMsg = chat?.content || "";
    const htmlContent = marked.parse(copyMsg);
    const htmlBlob = new Blob([htmlContent], { type: "text/html" });
    const textBlob = new Blob([copyMsg], { type: "text/plain" });
    const CItem = window["ClipboardItem"];
    navigator.clipboard
      .write([new CItem({ "text/html": htmlBlob, "text/plain": textBlob })])
      .then(() => $crud.notify({ type: "success", message: "Copied!" }));
  };

  const sendMessage = useCallback(async (text) => {
    const userMessage = (text || message).trim();
    if (!userMessage || isMessageSending) return;

    setIsMessageSending(true);
    setMessage("");
    setCurrentSuggestions([]);

    const userEntry = {
      id: `user-${Date.now()}`,
      role: "user",
      content: userMessage,
      createdAt: new Date().toISOString(),
      suggestions: [],
    };
    setChatHistory((prev) => [...prev, userEntry]);
    setTimeout(() => scrollToBottom(), 100);

    try {
      const { data } = await callAPI("/chat/kjuup-chat", {}, "POST", {
        message: userMessage,
      });

      const assistantContent = data?.reply || "";

      if (assistantContent) {
        const { cleanContent, suggestions } = parseSuggestions(assistantContent);
        const assistantEntry = {
          id: `assistant-${Date.now()}`,
          role: "assistant",
          content: cleanContent,
          createdAt: new Date().toISOString(),
          suggestions,
        };
        setChatHistory((prev) => {
          const updated = [...prev, assistantEntry];
          const filtered = filterDuplicateSuggestions(suggestions, updated);
          setCurrentSuggestions(filtered);
          return updated;
        });
      }

      setTimeout(() => scrollToBottom(), 150);
    } catch (error) {
      console.error("[KjuupSupport] Error:", error);
      const errEntry = {
        id: `error-${Date.now()}`,
        role: "assistant",
        content: isGerm
          ? "Entschuldigung, es gab einen Fehler. Bitte versuche es erneut."
          : "Sorry, there was an error. Please try again.",
        createdAt: new Date().toISOString(),
        suggestions: [],
      };
      setChatHistory((prev) => [...prev, errEntry]);
      setTimeout(() => scrollToBottom(), 100);
    } finally {
      setIsMessageSending(false);
    }
  }, [message, isMessageSending, isGerm, scrollToBottom]);

  const handleSuggestionClick = (suggestion) => {
    sendMessage(suggestion.label);
  };

  const handleNewChat = async () => {
    setChatHistory([]);
    setCurrentSuggestions(isGerm ? INITIAL_SUGGESTIONS_DE : INITIAL_SUGGESTIONS_EN);
    setMessage("");
    setExpandedMessages({});
    try {
      await callAPI("/chat/kjuup-chat/history", {}, "DELETE");
    } catch (e) {
      console.error("[KjuupSupport] Failed to clear history:", e);
    }
  };

  const renderMarkdown = (msg, messageId) => {
    const MAX_LEN = 800;
    const isExpanded = messageId ? expandedMessages[messageId] : true;
    const needsTruncation = messageId && msg && msg.length > MAX_LEN;
    const display = needsTruncation && !isExpanded ? msg.substring(0, MAX_LEN) : msg;

    return (
      <>
        <Markdown
          rehypePlugins={[rehypeRaw]}
          remarkPlugins={[remarkGfm, remarkBreaks]}
          skipHtml={false}
          components={{
            a: ({ href, children }) => (
              <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>
            ),
            h1: ({ children }) => <span className="ks-md-header">{children}</span>,
            h2: ({ children }) => <span className="ks-md-header">{children}</span>,
            h3: ({ children }) => <span className="ks-md-header">{children}</span>,
            h4: ({ children }) => <span className="ks-md-header">{children}</span>,
            ul: ({ children }) => <ul className="ks-md-list">{children}</ul>,
            ol: ({ children }) => <ol className="ks-md-list">{children}</ol>,
          }}
        >
          {display}
        </Markdown>
        {needsTruncation && !isExpanded && (
          <span className="ks-mehr-btn" onClick={() => toggleExpand(messageId)}>
            ...{isGerm ? "Mehr" : "More"}
          </span>
        )}
      </>
    );
  };

  const renderMessage = (chat) => {
    const isAssistant = chat.role === "assistant";

    return (
      <div key={chat.id} className={`ks-message ${isAssistant ? "ks-message--assistant" : "ks-message--user"}`}>
        <div
          className={`ks-bubble ${isAssistant ? "ks-bubble--assistant" : "ks-bubble--user"}`}
          onMouseEnter={() => isAssistant && setHoveredChatId(chat.id)}
          onMouseLeave={() => isAssistant && setHoveredChatId(null)}
        >
          {isAssistant && hoveredChatId === chat.id && (
            <button className="kj-btn kj-btn--icon kj-btn--sm kj-btn--neutral kj-btn--on-surface ks-copy-btn" onClick={() => handleCopy(chat)} title={strings.copy}>
              <MdContentCopy size={14} />
            </button>
          )}
          <div className="ks-bubble-content">
            {isAssistant ? renderMarkdown(chat.content, chat.id) : (
              <span className="kjuup-support-modal__user-text">{chat.content}</span>
            )}
          </div>
          <div className="ks-bubble-time">{moment(chat.createdAt).format("HH:mm")}</div>
        </div>
        {isAssistant && chat.suggestions && chat.suggestions.length > 0 && (
          <div className="ks-suggestions">
            {chat.suggestions.map((s, i) => (
              <button
                key={i}
                className="ks-suggestion-chip"
                onClick={() => handleSuggestionClick(s)}
                disabled={isMessageSending}
              >
                {s.label}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  if (!user || !featureFlags.enableSupportAgent) return null;

  const modalContent = (
    <>
      {/* Support Agent Header Button */}
      {!isOpen && (
        <div className="ks-fab-wrapper">
          <span className="ks-fab-tooltip">
            {isGerm ? "Frag den Agenten" : "Ask the Agent"}
          </span>
          <button className="ks-fab" onClick={handleOpen}>
            <img
              src={process.env.PUBLIC_URL + "assets/img/SupportAgent.png"}
              alt="Kjuup Support"
              className="ks-fab-logo"
            />
          </button>
        </div>
      )}

      {/* Modal Overlay */}
      {isOpen && (
        <div className={`ks-overlay ${isClosing ? "ks-overlay--closing" : ""}`} onClick={handleClose}>
          <div
            ref={modalRef}
            className={`ks-modal ${isClosing ? "ks-modal--closing" : ""}`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="ks-header">
              <div className="ks-header-left">
                <span className="ks-header-ai-icon">
                  <img
                    src={process.env.PUBLIC_URL + "assets/img/SupportAgent.png"}
                    alt="Kjuup Support"
                    width={24}
                    height={24}
                    className="kjuup-support-modal__agent-img"
                  />
                </span>
                <div className="ks-header-text">
                  <h3 className="ks-header-title">Kjuup Support</h3>
                  <span className="ks-header-subtitle">
                    {isGerm ? "Wie können wir helfen?" : "How can we help?"}
                  </span>
                </div>
              </div>
              <div className="ks-header-actions">
                <button className="ks-header-btn" onClick={handleNewChat} title={isGerm ? "Neuer Chat" : "New chat"}>
                  <span className="material-symbols-outlined kjuup-support-modal__header-icon">add_circle</span>
                </button>
                <button className="ks-header-btn" onClick={handleClose}>
                  <MdClose size={20} />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div className="ks-messages">
              {chatHistory.length === 0 && !isMessageSending && (
                <div className="ks-empty-state">
                  <span className="ks-empty-ai-icon">
                    <img
                      src={process.env.PUBLIC_URL + "assets/img/SupportAgent.png"}
                      alt="Kjuup Support"
                      width={40}
                      height={40}
                      className="kjuup-support-modal__agent-img"
                    />
                  </span>
                  <p className="ks-empty-text">
                    {isGerm
                      ? "Willkommen beim Kjuup Support! Stell deine Frage oder wähle ein Thema aus."
                      : "Welcome to Kjuup Support! Ask a question or pick a topic below."}
                  </p>
                  {currentSuggestions.length > 0 && (
                    <div className="ks-initial-suggestions">
                      {currentSuggestions.map((s, i) => (
                        <button
                          key={i}
                          className="ks-suggestion-chip ks-suggestion-chip--initial"
                          onClick={() => handleSuggestionClick(s)}
                        >
                          {s.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {chatHistory.map((chat) => renderMessage(chat))}

              {isMessageSending && (
                <div className="ks-message ks-message--assistant">
                  <div className="ks-bubble ks-bubble--assistant ks-bubble--loading">
                    <div className="ks-typing-indicator">
                      <span /><span /><span />
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Bottom suggestions (latest) */}
            {!isMessageSending && currentSuggestions.length > 0 && chatHistory.length > 0 && (
              <div className="ks-bottom-suggestions">
                {currentSuggestions.map((s, i) => (
                  <button
                    key={i}
                    className="ks-suggestion-chip ks-suggestion-chip--bottom"
                    onClick={() => handleSuggestionClick(s)}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            )}

            {/* Input */}
            <div className="ks-input-area">
              <div className="ks-input-wrapper">
                <textarea
                  ref={textRef}
                  className="ks-input"
                  value={message}
                  placeholder={isGerm ? "Frag mich etwas..." : "Ask me anything..."}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      if (message.trim()) sendMessage();
                    }
                  }}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={1}
                  disabled={isMessageSending}
                />
                <button
                  className={`ks-send-btn ${message.trim() && !isMessageSending ? "ks-send-btn--active" : ""}`}
                  onClick={() => message.trim() && sendMessage()}
                  disabled={!message.trim() || isMessageSending}
                >
                  <span className="material-symbols-outlined kjuup-support-modal__send-icon">send</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );

  return ReactDOM.createPortal(modalContent, document.body);
}

export default memo(KjuupSupportModal);
